import type { LayerDef, Legend } from "../types";

/**
 * How each commune's population has changed, as a choropleth.
 *
 * Every census period ships as its own attribute on the same feature —
 * `g1975`…`g2023` for total growth, `mig` and `nat` for the two components —
 * exactly like the price tiles carry `p2021`…`p2025`. Switching the metric or
 * the period is then a paint expression over geometry the browser already
 * holds: no refetch, no re-parse.
 *
 * Rates are stored as percent per year rather than as fractions, so the numbers
 * in the tile read the same way as the numbers in the legend.
 */

/** Census dates the série historique covers, as the periods between them. */
export const CENSUS_PERIODS = [
  { from: 1968, to: 1975 },
  { from: 1975, to: 1982 },
  { from: 1982, to: 1990 },
  { from: 1990, to: 1999 },
  { from: 1999, to: 2007 },
  { from: 2007, to: 2012 },
  { from: 2012, to: 2017 },
  { from: 2017, to: 2023 },
];

export const periodLabel = (to: number): string => {
  const p = CENSUS_PERIODS.find((x) => x.to === to);
  return p ? `${p.from}–${p.to}` : String(to);
};

export type MapMetricId =
  | "growth"
  | "migration"
  | "natural"
  | "vacancy"
  | "second"
  | "density";

interface MapMetric {
  label: string;
  /** Shown above the legend. */
  unit: string;
  /** Which tile attribute holds it, for the period in force. */
  attr: (period: number) => string;
  /** Whether the metric is read over a period or is a single-date stock. */
  periodic: boolean;
  stops: { value: number; color: string }[];
  format: (v: number) => string;
  note?: string;
}

/**
 * Diverging red→blue for the three rate metrics, because zero is a real
 * boundary: a commune losing people and one gaining them are not two shades of
 * the same thing. Growing is blue rather than green so the layer cannot be
 * confused with the green index.
 */
const RATE_STOPS = [
  { value: -1.5, color: "#b2182b" },
  { value: -0.5, color: "#ef8a62" },
  { value: -0.1, color: "#fddbc7" },
  { value: 0.1, color: "#f7f7f7" },
  { value: 0.5, color: "#d1e5f0" },
  { value: 1.5, color: "#67a9cf" },
  { value: 3, color: "#2166ac" },
];

const signedPct = (v: number) => `${v > 0 ? "+" : v < 0 ? "−" : ""}${Math.abs(v)}%`;

export const MAP_METRICS: Record<MapMetricId, MapMetric> = {
  growth: {
    label: "Population change",
    unit: "%/yr",
    attr: (p) => `g${p}`,
    periodic: true,
    stops: RATE_STOPS,
    format: signedPct,
  },
  migration: {
    label: "Net migration",
    unit: "%/yr",
    attr: (p) => `mig${p}`,
    periodic: true,
    stops: RATE_STOPS,
    format: signedPct,
    note: "People arriving minus people leaving — the part that can turn around quickly.",
  },
  natural: {
    label: "Natural change",
    unit: "%/yr",
    attr: (p) => `nat${p}`,
    periodic: true,
    stops: RATE_STOPS,
    format: signedPct,
    note: "Births minus deaths — largely already determined by today's age pyramid.",
  },
  vacancy: {
    label: "Vacant homes",
    unit: "% of dwellings",
    attr: () => "vacancy_rate",
    periodic: false,
    stops: [
      { value: 2, color: "#f7f7f7" },
      { value: 6, color: "#d9d3e8" },
      { value: 9, color: "#b3a2d4" },
      { value: 12, color: "#8c6bb1" },
      { value: 18, color: "#6e016b" },
    ],
    format: (v) => `${v}%`,
    note: "Slack in the stock: a high rate absorbs arrivals without prices moving.",
  },
  second: {
    label: "Second homes",
    unit: "% of dwellings",
    attr: () => "second_rate",
    periodic: false,
    stops: [
      { value: 1, color: "#f7f7f7" },
      { value: 4, color: "#fee0b6" },
      { value: 8, color: "#fdb863" },
      { value: 15, color: "#e08214" },
      { value: 25, color: "#b35806" },
    ],
    format: (v) => `${v}%`,
    note: "Where this is high the resident count can fall while prices climb.",
  },
  density: {
    label: "Density",
    unit: "people/km²",
    attr: () => "density",
    periodic: false,
    stops: [
      { value: 100, color: "#f7f7f7" },
      { value: 1000, color: "#d9e8d3" },
      { value: 4000, color: "#a1d99b" },
      { value: 10000, color: "#41ab5d" },
      { value: 25000, color: "#005a32" },
    ],
    format: (v) => (v >= 1000 ? `${v / 1000}k` : String(v)),
  },
};

/** Fill colour for a metric over a period, transparent where there is no data. */
export function demographicsColor(metric: MapMetricId, period: number): unknown[] {
  const def = MAP_METRICS[metric];
  const value = ["get", def.attr(period)];
  return [
    "case",
    ["==", value, null],
    "rgba(0,0,0,0)",
    ["interpolate", ["linear"], value, ...def.stops.flatMap((s) => [s.value, s.color])],
  ];
}

export function demographicsLegend(metric: MapMetricId, period: number): Legend {
  const def = MAP_METRICS[metric];
  const stops = def.stops.map((s, i) => {
    const next = def.stops[i + 1];
    return {
      color: s.color,
      label: next ? `${def.format(s.value)} – ${def.format(next.value)}` : `${def.format(s.value)}+`,
    };
  });
  return {
    unit: def.periodic ? `${def.label}, ${def.unit} · ${periodLabel(period)}` : `${def.label}, ${def.unit}`,
    stops,
    note: def.note ?? "Metric and period are set in the People pane.",
  };
}

export const DEMOGRAPHICS_FILL = "demographics-fill";

const DEFAULT_PERIOD = CENSUS_PERIODS[CENSUS_PERIODS.length - 1].to;

export const demographicsLayer: LayerDef = {
  id: "demographics",
  label: "Population change",
  group: "people",
  description:
    "How each city's population has moved between censuses, and what drove it — births and deaths, or people arriving and leaving.",
  attribution:
    '<a href="https://www.data.gouv.fr/datasets/serie-historique-du-recensement-de-la-population">Série historique du recensement · INSEE</a>',
  defaultVisible: false,
  legend: demographicsLegend("growth", DEFAULT_PERIOD),
  legendFor: (ctx) =>
    demographicsLegend(ctx.demographicsMetric as MapMetricId, ctx.demographicsPeriod),
  sources: {
    demographics: {
      type: "vector",
      url: "pmtiles:///tiles/demographics_commune.pmtiles",
    },
  },
  layers: [
    {
      id: DEMOGRAPHICS_FILL,
      type: "fill",
      source: "demographics",
      "source-layer": "demographics",
      paint: {
        "fill-color": demographicsColor("growth", DEFAULT_PERIOD) as never,
        "fill-opacity": 0.7,
      },
    },
    {
      id: "demographics-line",
      type: "line",
      source: "demographics",
      "source-layer": "demographics",
      paint: { "line-color": "#ffffff", "line-width": 0.6, "line-opacity": 0.7 },
    },
  ],
};
